import React, { useMemo, useCallback, useState, useEffect } from 'react'
import { getAddress } from 'viem'

// 单个代币查询组件 - 这是一个逻辑组件，不渲染UI
interface TokenQueryComponentProps {
  token: {
    tokenAddress: string
    decimals: number
    symbol: string
    contractAddress?: string // spender for allowance; if absent, fallback to props.contractAddress
  }
  account?: string
  chainId?: number
  contractAddress?: string
  wagmiHooks?: {
    useBalance: any
    useReadContract: any
    useAccount: any
  }
  onResult: (
    tokenAddress: string,
    result: {
      balance: any
    }
  ) => void
}

const TokenQueryComponent: React.FC<TokenQueryComponentProps> = ({
  token,
  account,
  chainId,
  wagmiHooks,
  onResult,
}) => {
  // 余额查询
  const balance = wagmiHooks?.useBalance?.({
    address: account,
    token: token.tokenAddress,
    chainId: chainId,
    query: {
      enabled:
        !!account &&
        !!chainId &&
        !!token.tokenAddress &&
        !!wagmiHooks?.useBalance,
    },
  })

  // 当结果变化时，通知父组件
  useEffect(() => {
    onResult(getAddress(token.tokenAddress), { balance })
  }, [balance, token.tokenAddress, onResult])

  return null // 逻辑组件，不渲染任何UI
}

interface BalanceResult {
  address: string
  value: bigint
  formatted: string
  decimals: number
}

interface MulticallBalanceResult {
  balanceList: BalanceResult[]
  baseTokenBalance: {
    value: bigint
    formatted: string
    decimals: number
    symbol: string
  }
  combinedData: Record<
    string,
    {
      balance: BalanceResult
    }
  >
}

interface UseMulticallBalanceParams {
  account?: string
  chainId?: number | string
  contractAddress?: string
  tokens?: Array<{
    tokenAddress: string
    decimals: number
    symbol: string
  }>
  // 外部传入的 wagmi hooks
  wagmiHooks?: {
    useBalance: any
    useReadContract: any
    useAccount: any
  }
}

export const useMulticallBalance = ({
  account,
  chainId,
  contractAddress,
  tokens = [],
  wagmiHooks,
}: UseMulticallBalanceParams) => {
  // 统一规范 chainId，避免传入字符串导致 wagmi useBalance 查询失败
  const normalizedChainId: number | undefined =
    typeof chainId === 'string'
      ? Number.isFinite(Number(chainId))
        ? Number(chainId)
        : undefined
      : chainId
  // 过滤有效的代币
  const validErcTokens = useMemo(() => {
    const ZERO = '0x0000000000000000000000000000000000000000'
    const EE = '0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee'
    return tokens.filter(token => {
      const addr = token.tokenAddress
      if (!addr) return false
      const lower = addr.toLowerCase()
      // 过滤基础币(ETH)和零地址，避免走 ERC20 查询
      if (addr === ZERO) return false
      if (lower === EE) return false
      return true
    })
  }, [tokens])

  // 存储代币查询结果
  const [tokenResults, setTokenResults] = useState<
    Record<
      string,
      {
        balance: any
      }
    >
  >({})

  // 处理单个代币查询结果的回调
  const handleTokenResult = useCallback(
    (
      tokenAddress: string,
      result: {
        balance: any
      }
    ) => {
      setTokenResults(prev => ({
        ...prev,
        [tokenAddress]: result,
      }))
    },
    []
  )

  // 使用外部传入的 useBalance hook 获取基础代币余额
  const baseTokenBalance = wagmiHooks?.useBalance?.({
    address: account,
    chainId: normalizedChainId,
    query: {
      enabled: !!account && !!normalizedChainId && !!wagmiHooks?.useBalance,
    },
  })

  // 允许外部触发刷新（通过变更 key 重新挂载 TokenQueryComponent 以强制 refetch）
  const [refreshNonce, setRefreshNonce] = useState(0)

  // 创建代币查询组件列表
  const queryComponents = useMemo(() => {
    return validErcTokens.map(token =>
      React.createElement(TokenQueryComponent, {
        key: `${token.tokenAddress}-${refreshNonce}`,
        token,
        account,
        chainId: normalizedChainId,
        contractAddress,
        wagmiHooks,
        onResult: handleTokenResult,
      })
    )
  }, [
    validErcTokens,
    account,
    normalizedChainId,
    contractAddress,
    wagmiHooks,
    handleTokenResult,
    refreshNonce,
  ])

  // 组合所有数据
  const multicallResult = useMemo((): MulticallBalanceResult => {
    // 处理基础代币余额
    const baseBalance = baseTokenBalance?.data
      ? {
          value: baseTokenBalance.data.value,
          formatted: baseTokenBalance.data.formatted,
          decimals: baseTokenBalance.data.decimals,
          symbol: baseTokenBalance.data.symbol,
        }
      : {
          value: BigInt(0),
          formatted: '0',
          decimals: 18,
          symbol: 'ETH',
        }

    // 处理 ERC20 代币余额（基于 tokenResults 状态）
    const balanceList: BalanceResult[] = validErcTokens
      .map(token => {
        const result = tokenResults[getAddress(token.tokenAddress)]
        const balance = result?.balance
        const data = balance?.data

        return {
          address: getAddress(token.tokenAddress),
          value: data?.value || BigInt(0),
          formatted: data?.formatted || '0',
          decimals: data?.decimals || token.decimals,
        }
      })
      .filter(result => result.address) // 过滤掉无效结果

    // 合并数据
    const combinedData = balanceList.reduce(
      (prev, balance) => {
        return {
          ...prev,
          [balance.address]: {
            balance,
          },
        }
      },
      {} as Record<string, { balance: BalanceResult }>
    )

    return {
      balanceList,
      baseTokenBalance: baseBalance,
      combinedData,
    }
  }, [baseTokenBalance, tokenResults, validErcTokens])

  // 提供一个简单的查询函数接口（保持向后兼容）
  const multicallRun =
    useCallback(async (): Promise<MulticallBalanceResult> => {
      return multicallResult
    }, [multicallResult])

  // 计算加载状态
  const isLoading = useMemo(() => {
    const baseLoading = baseTokenBalance?.isLoading || false
    const tokenLoading = Object.values(tokenResults).some(
      result => result.balance?.isLoading
    )
    return baseLoading || tokenLoading
  }, [baseTokenBalance?.isLoading, tokenResults])

  const multicallRefresh = useCallback(() => setRefreshNonce(n => n + 1), [])

  return {
    multicallRun,
    multicallRefresh,
    multicallResult,
    validErcTokens,
    isLoading,
    // 返回查询组件，需要在使用时渲染
    queryComponents,
  }
}
